import React, { useEffect, useMemo, useReducer, useState } from "react"
import { Portal } from "react-portal"
import { connect } from "react-redux"
import { useTranslation } from "react-i18next"
import Loader from "react-loader-spinner"
import { IOrganisation } from "@typed/api/responses/Organisation"
import { IWorkDetails } from "@typed/api/responses/WorkDetails"
import { TConnectedProps } from "@typed/redux/CombinedProps"
import { clearItemsSelection } from "@store/ducks/inventory"
import { emitAnalyticsEvent } from "@store/ducks/analytics/actions"
import { TAnalyticsEventEmitter } from "@services/analytics/definitions"
import { selectedPages } from "@services/pdf/generator"
import { getFilteredItems, getInventoryItemsEntities } from "@store/ducks/inventory/selectors"
import { areOrganisationsLoading, getOrganisations } from "@store/ducks/orgs/selectors"
import { fetchUserOrgsRequest } from "@store/ducks/orgs/actions"
import { generateDocumentName } from "@services/pdf/documentName"
import useKeyDown from "@enhancers/useKeyDown"
import { ReactComponent as CloseIcon } from "@icons/CloseIcon.svg"
import * as styles from "./PdfBuilder.module.scss"
import { BuilderProvider } from "./context"
import { CompositionPane } from "./CompositionPane"
import { OptionsPanel } from "./OptionsPanel"
import { IPreviewState, PreviewStep, usePreviewDocument } from "./PreviewStep"
import { IBuilderAssets, useBuilderAssets } from "./hooks/useBuilderAssets"
import { usePdfStrings } from "./hooks/usePdfStrings"
import { buildRows } from "./state/buildRows"
import { builderReducer, IBuilderState } from "./state/reducer"

type TOwnProps = {
	itemsIds: string[]
	onClose: () => void
	/** clear the bulk selection after export (multi-select flow); default true */
	clearSelectionOnExport?: boolean
}

type TStateProps = {
	works: IWorkDetails[]
	org: IOrganisation | null
	orgsLoading: boolean
}

type TDispatchProps = {
	clearSelection: () => void
	trackEvent: TAnalyticsEventEmitter
	refetchOrgs: () => void
}

type TProps = TConnectedProps<TOwnProps, TStateProps, TDispatchProps>

const BuilderInner: React.FC<{
	works: IWorkDetails[]
	org: IOrganisation | null
	assets: IBuilderAssets
	onClose: () => void
	onExported: () => void
	trackEvent: TAnalyticsEventEmitter
}> = ({ works, org, assets, onClose, onExported, trackEvent }) => {
	const { t } = useTranslation()
	const strings = usePdfStrings()

	const initialState: IBuilderState = useMemo(() => {
		const { coverRow, contentRows } = buildRows({
			works,
			org,
			coverTemplate: assets.coverTemplate,
			detailsTemplate: assets.detailsTemplate,
			attachments: assets.attachments,
			taxMessage: strings.taxMessage,
		})
		return {
			step: "builder",
			coverRow,
			contentRows,
			options: { activeOptions: ["branded"], hasBrand: assets.hasBrand },
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const [state, dispatch] = useReducer(builderReducer, initialState)
	const preview = usePreviewDocument(state.step === "preview", state, assets, strings, org?.pdf_layout_type ?? "")

	// iOS-parity analytics
	useEffect(() => {
		trackEvent({ name: "PDF load custom page" })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (state.step !== "preview") {
			return
		}
		const pages = selectedPages(state.contentRows)
		let infoCount = 0
		let fillCount = 0
		let fitCount = 0
		let attachmentCount = 0
		pages.forEach(({ page }) => {
			if (page.type.kind === "imageInfo" || page.type.kind === "wcImageInfo") {
				infoCount += 1
			} else if (page.type.kind === "image") {
				if (page.type.fill) {
					fillCount += 1
				} else {
					fitCount += 1
				}
			} else if (page.type.kind === "attachment") {
				attachmentCount += 1
			}
		})
		trackEvent({
			name: "PDFOptions",
			payload: {
				branded: `${state.options.activeOptions.includes("branded")}`,
				price: `${state.options.activeOptions.includes("price")}`,
				note: `${state.options.activeOptions.includes("notes")}`,
				provenance: `${state.options.activeOptions.includes("provenance")}`,
				exhibitions: `${state.options.activeOptions.includes("exhibition")}`,
				literature: `${state.options.activeOptions.includes("literature")}`,
			},
		})
		trackEvent({
			name: "PDF load preview start",
			payload: {
				"Page count": `${pages.length}`,
				"Info page count": `${infoCount}`,
				"Fill page count": `${fillCount}`,
				"Fit page count": `${fitCount}`,
				"Attachment count": `${attachmentCount}`,
			},
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.step])

	useEffect(() => {
		if (state.step === "preview" && preview.status === "ready" && preview.result) {
			trackEvent({
				name: "PDF load preview end",
				payload: { "Page count": `${preview.result.selectedPageCount}` },
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preview.status])

	const documentTitle = useMemo(() => {
		const artists = state.contentRows
			.filter((row) => row.rowType === "artwork")
			.flatMap((row) => row.pages.map(() => row.artist ?? ""))
			.filter((artist) => artist !== "")
		return generateDocumentName(state.coverRow.customInput, artists)
	}, [state.contentRows, state.coverRow.customInput])

	const exportPdf = (previewState: IPreviewState) => {
		if (!previewState.result) {
			return
		}
		const blob = new Blob([previewState.result.bytes], { type: "application/pdf" })
		const url = URL.createObjectURL(blob)
		const anchor = document.createElement("a")
		anchor.href = url
		anchor.download = `${documentTitle}.pdf`
		document.body.appendChild(anchor)
		anchor.click()
		document.body.removeChild(anchor)
		window.setTimeout(() => URL.revokeObjectURL(url), 10000)
		trackEvent({
			name: "Export Works",
			payload: {
				"Export Type": "PDF",
				"Number Of Works": `${works.length}`,
			},
		})
		onExported()
	}

	const isPreview = state.step === "preview"

	return (
		<BuilderProvider
			value={{
				state,
				dispatch,
				assets,
				strings,
				layoutType: org?.pdf_layout_type ?? "",
				documentTitle,
				onClose,
				onExported,
			}}
		>
			<div className={styles.header}>
				<span className={styles.headerTitle}>{isPreview ? t("pdfBuilder.preview") : t("pdfBuilder.createTitle")}</span>
				<button type="button" className={styles.closeButton} onClick={onClose} aria-label={t("common.close")}>
					<CloseIcon />
				</button>
			</div>

			{isPreview ? (
				<PreviewStep preview={preview} />
			) : (
				<div className={styles.body}>
					<OptionsPanel />
					<CompositionPane />
				</div>
			)}

			<div className={styles.footer}>
				{isPreview && (
					<span className={styles.footerInfo}>
						{t("pdfBuilder.exportsAs")} {documentTitle}.pdf
					</span>
				)}
				{isPreview ? (
					<>
						<button
							type="button"
							className={styles.buttonBright}
							onClick={() => dispatch({ type: "setStep", step: "builder" })}
							data-testid="pdf-back"
						>
							{t("pdfBuilder.back")}
						</button>
						<button
							type="button"
							className={styles.buttonDark}
							onClick={() => exportPdf(preview)}
							disabled={preview.status !== "ready"}
							data-testid="pdf-export"
						>
							{t("pdfBuilder.export")}
						</button>
					</>
				) : (
					<>
						<button type="button" className={styles.buttonBright} onClick={onClose} data-testid="pdf-cancel">
							{t("pdfBuilder.cancel")}
						</button>
						<button
							type="button"
							className={styles.buttonDark}
							onClick={() => dispatch({ type: "setStep", step: "preview" })}
							data-testid="pdf-preview"
						>
							{t("pdfBuilder.preview")}
						</button>
					</>
				)}
			</div>
		</BuilderProvider>
	)
}

export const PdfBuilderOverlay: React.FC<TProps["Combined"]> = ({
	onClose,
	works,
	org,
	orgsLoading,
	clearSelection,
	trackEvent,
	refetchOrgs,
	clearSelectionOnExport = true,
}) => {
	const { t } = useTranslation()
	const artists = useMemo(
		() => works.map((work) => work.artist).filter((artist, i, arr) => arr.indexOf(artist) === i),
		[works]
	)

	// Don't build the PDF until the org fetch has settled — building with a
	// not-yet-loaded org silently produces a default-layout (unbranded) PDF for
	// a White Cube user. If the org fetch never ran, or previously failed
	// silently, kick it off once on open.
	const ensuredOrgFetch = React.useRef(false)
	useEffect(() => {
		if (!org && !orgsLoading) {
			refetchOrgs()
		}
		ensuredOrgFetch.current = true
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	const waitingForOrgs = orgsLoading || (!org && !ensuredOrgFetch.current)

	const assets = useBuilderAssets(org, artists)
	const [exported, setExported] = useState(false)

	// desktop convention: Escape closes the overlay. When the user is typing
	// (cover title, price override) Escape only blurs the field — closing
	// would silently discard the whole composition.
	useKeyDown("Escape", () => {
		const active = document.activeElement
		if (
			active instanceof HTMLElement &&
			(active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable)
		) {
			active.blur()
			return
		}
		onClose()
	})

	const onExported = () => {
		if (!exported) {
			setExported(true)
			if (clearSelectionOnExport) {
				clearSelection()
			}
			onClose()
		}
	}

	return (
		<Portal>
			<div className={styles.overlay} data-testid="pdf-builder-overlay" role="dialog" aria-modal="true">
				{waitingForOrgs || assets.loading ? (
					<>
						<div className={styles.header}>
							<span className={styles.headerTitle}>{t("pdfBuilder.createTitle")}</span>
							<button type="button" className={styles.closeButton} onClick={onClose} aria-label={t("common.close")}>
								<CloseIcon />
							</button>
						</div>
						<div className={styles.centerLoading}>
							<Loader type="Oval" color="#282828" height={40} width={40} />
							<span>{t("pdfBuilder.loadingAssets")}</span>
						</div>
					</>
				) : (
					<BuilderInner
						works={works}
						org={org}
						assets={assets}
						onClose={onClose}
						onExported={onExported}
						trackEvent={trackEvent}
					/>
				)}
			</div>
		</Portal>
	)
}

const mapStateToProps: TProps["State"] = (state, ownProps) => {
	const entities = getInventoryItemsEntities(state)
	const filteredOrder: string[] = getFilteredItems(state)

	// rows follow the current sort order of the works list; missing items excluded
	const orderedIds = [
		...filteredOrder.filter((id) => ownProps.itemsIds.includes(id)),
		...ownProps.itemsIds.filter((id) => !filteredOrder.includes(id)),
	]

	const works = orderedIds.map((id) => entities[id]).filter((work): work is IWorkDetails => Boolean(work))

	const orgs: IOrganisation[] = getOrganisations(state)

	return { works, org: orgs[0] ?? null, orgsLoading: areOrganisationsLoading(state) }
}

const mapDispatchToProps: TProps["Dispatch"] = (dispatch) => ({
	clearSelection: () => dispatch(clearItemsSelection()),
	trackEvent: (event) => dispatch(emitAnalyticsEvent(event)),
	refetchOrgs: () => dispatch(fetchUserOrgsRequest()),
})

export default connect(mapStateToProps, mapDispatchToProps)(PdfBuilderOverlay)
